export default function MonroeTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Monroe Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Monroe travelers actually move helps us anticipate the passport urgencies that arise here. From medical conference deadlines to agricultural trade missions, Northeast Louisiana travel operates on a rhythm driven by healthcare, academia, and agribusiness cycles.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-heart-pulse-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Medical Conferences &amp; Global Health</h3>
                <p className="text-gray-400 text-xs">Vienna, Geneva, London, Toronto, Montreal</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Monroe's medical corridor employs hundreds of physicians and healthcare professionals who attend international CME conferences, global health summits, and specialty medical symposia. Conference invitations and CME deadlines often arrive one to three months before the event, and physicians frequently discover their passport has expired — especially those who paused international travel during the pandemic years. International clinical partnerships also drive short-notice travel.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Many EU countries require six months passport validity beyond your return date. Check your expiration the moment a conference registration or travel booking is confirmed.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-seedling-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Agricultural Trade &amp; Business Travel</h3>
                <p className="text-gray-400 text-xs">Brazil, Argentina, Canada, Germany — commodity and equipment</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Northeast Louisiana's cotton, soybean, and specialty crop producers maintain business relationships with international buyers, seed suppliers, and equipment manufacturers. Agricultural trade missions, commodity buyer meetings, and equipment training sessions in Brazil, Argentina, Canada, and Germany can materialize with two to three weeks notice. Many rural parish farmers and agribusiness owners have not traveled internationally in years and may hold expired passports.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Brazil requires a valid U.S. visa in addition to a passport. If your business trip includes Brazil, start the visa process simultaneously with your passport renewal — visa processing adds additional lead time.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">ULM Study Abroad &amp; Clinical Rotations</h3>
                <p className="text-gray-400 text-xs">Europe, Latin America, international clinical sites</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              University of Louisiana Monroe pharmacy, nursing, and health sciences students participate in international clinical rotations. Business, social sciences, and liberal arts students pursue exchange programs in Europe and Latin America. Many ULM students are first-time passport applicants who discover the acceptance clerk requirement only after accepting a placement. Academic calendar deadlines — spring orientation in January, fall orientation in August — do not flex.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> ULM pharmacy students completing international rotations often need their passport six to eight weeks before departure to also obtain any required country-specific visas. Apply immediately after placement acceptance.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Caribbean Cruises &amp; Family Travel</h3>
                <p className="text-gray-400 text-xs">New Orleans cruise port, DFW connections</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Monroe families regularly book Caribbean cruises from the Port of New Orleans (290 miles south) or connect through Dallas-Fort Worth for transatlantic travel. Holiday cruise deals and summer vacation bookings often come together quickly, and passport oversight is one of the most common pre-cruise crises we resolve for Northeast Louisiana families. Children who have never held a passport require the most lead time.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Children's passports (under 16) require both parents to appear in person at acceptance. Build in extra lead time — child passport applications cannot be expedited without the in-person step first.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-flight-takeoff-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Monroe Regional Airport (MLU) &amp; International Connections</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Monroe Regional Airport (MLU) connects Northeast Louisiana travelers to Dallas-Fort Worth, Atlanta, and other major hubs for international connections. Many Monroe residents also drive to Shreveport Regional (SHV, ~90 miles) or connect through DFW for transatlantic and international routes. Whether departing from MLU or a connecting hub, your passport must be valid and in hand before your flight date.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}